<template>
    <div class="gallery-filters" ref="filters">
        <!-- Filters button -->
        <div class="btn-group" :class="{open: filtersOpen}">
            <button
                ref="button"
                type="button"
                class="btn btn-filter" :class="{'btn-default': !filterDirty, 'btn-inverse': filterDirty}"
                @click="filtersOpen = !filtersOpen"
                :disabled="disabled"
            >
                <i class="fas fa-filter"></i>
                Filter
                <span v-if="filterDirty" class="badge badge-inverse">{{ filterCount }}</span>
                <span class="caret"></span>
            </button>
            <div v-show="filtersOpen" class="mega-menu dropdown-menu dropdown-menu-right" ref="menu">
                <div class="mega-menu-header justify-content-end align-items-center flex-nowrap mb-4">
                    <div class="filter-search d-flex flex-inline flex-grow align-items-center">
                        <label class="text-uppercase font-weight-bold mb-0 mr-4">Search</label>
                        <input class="form-control" type="text" v-model="searchValue" @input="setTextSearch($event.target.value)" />
                    </div>
                    <button class="btn btn-transparent btn-clear-filters" @click="$emit('clear-filters')" :disabled="!filterDirty">
                        <i class="fas fa-times"></i> Reset all filters
                    </button>
                </div>
                <div class="mega-menu-content">
                    <div
                        class="filter-group"
                        :class="groupClassName(group)"
                        v-for="(group) in filterOptions"
                        :key="group.name"
                        v-show="!group.condition || (group.condition && checkCondition(group.condition))"
                    >
                        <!-- Checkboxes //-->
                        <template v-if="group.type === 'checkbox'">
                            <h4 v-if="group.label">{{ group.label }}</h4>
                            <label
                                class="form-check-label"
                                :class="filterClassName(filter)" v-for="(filter, index) in group.filters"
                                :key="index"
                                :for="filterId(filter, group, index)"
                            >
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    :id="filterId(filter, group, index)"
                                    :value="filter.value"
                                    :checked="isActive(filter.value)"
                                    :disabled="isActive(filter.disabled)"
                                    @change="setFilter($event, filter, group)">
                                <span v-html="filter.text"></span>
                            </label>
                        </template>
                        <!-- Radios //-->
                        <template v-if="group.type === 'radio'">
                            <h4 v-if="group.label">{{ group.label }}</h4>
                            <label
                                class="form-check-label"
                                :class="filterClassName(filter)"
                                v-for="(filter, index) in group.filters"
                                :key="index"
                                :for="filterId(filter, group, index)"
                            >
                                <input
                                    class="form-check-input"
                                    type="radio"
                                    :id="filterId(filter, group, index)"
                                    :value="filter.value"
                                    :checked="isActive(filter.value)"
                                    :disabled="isActive(filter.disabled)"
                                    @change="setFilter($event, filter, group)">
                                <span v-html="filter.text"></span>
                            </label>
                        </template>
                    </div>
                </div>
                <div class="mega-menu-footer">
                    <button class="btn btn-primary btn-block btn-apply-filters text-uppercase" @click="$emit('apply-filters')" :disabled="!filterDirty">Apply filters</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    kebabCase,
    camelCase,
    startCase,
    has,
    find,
    isEqual,
    isBoolean,
    debounce,
} from 'lodash';

export default {
    name: 'Filters',
    props: {
        disabled: {
            tyoe: Boolean,
            required: false,
        },
        filterOptions: {
            type: Array,
            required: true,
        },
        currentFilter: {
            type: Array,
            required: true,
        },
        defaultFilter: {
            type: Array,
            required: true,
        },
        filterDirty: {
            type: Boolean,
            required: true,
        },
        filterCount: {
            type: Number,
            required: true,
        },
        setFilter: {
            type: Function,
            required: true,
        },
        disableGroups: {
            type: Function,
            required: true,
        },
        textSearch: {
            required: true,
            validator: prop => typeof prop === 'string' || prop === null,
        },
    },
    data() {
        return {
            filtersOpen: false,
            searchValue: '',
        }
    },
    watch: {
        textSearch: {
            handler(newValue) {
                this.searchValue = newValue;
            },
        },
    },
    mounted() {
        document.addEventListener('keyup', (e) => {
            if (e.keyCode === 27) {
                this.filtersOpen = false;
            }
        });

        document.addEventListener('click', (e) => {
            const allowClose = !isEqual(e.target, this.$refs.menu) &&
                !isEqual(e.target, this.$refs.button) &&
                (this.$refs.menu && !this.$refs.menu.contains(e.target)) &&
                (this.$refs.button && !this.$refs.button.contains(e.target));

            if (allowClose) {
                this.filtersOpen = false
            }
        });

         this.disableGroups();
    },
    methods: {
        isActive(filter) {
            if (isBoolean(filter)) {
                return filter;
            } else {
                return this.currentFilter.some((setFilter) => {
                    if (isEqual(filter, setFilter)) {
                        return true;
                    }
                    return false;
                });
            }
        },
        checkCondition(condition) {
            let passes = false;
            if (Array.isArray(condition)) {
                for (let filter of condition) {
                    if (passes = find(this.currentFilter, filter)) {
                        break;
                    }
                }
            } else {
                passes = find(this.currentFilter, condition);
            }
            return passes;
        },
        groupSelectedCount(group) {
            let count = 0;
            const filters = group.filters.map(filter => filter.value);
            filters.forEach(filter => {
                if (find(this.currentFilter, filter)) count++;
            });
            return count;
        },
        groupClassName(group) {
            const classes = {
                'is-disabled': has(group, 'disabled') && this.checkCondition(group.disabled),
            };
            classes[`filter-group-${group.type}`] = true;
            return classes;
        },
        filterClassName(filter) {
            const classes = {
                'is-disabled': this.isActive(filter.disabled),
            };
            classes[`filter-${kebabCase(filter.text)}`] = true;
            return classes;
        },
        filterId(filter, group, index) {
            return camelCase(`filter${group.label}${filter.text}${index}`);
        },
        setTextSearch: debounce(function(value) {
            this.$emit('text-search', value);
        }, 1000),
        startCase,
    }
}
</script>

<style lang="scss" scoped>
@import '~/sass/_variables';

.gallery-filters {
    display: flex;
    flex-direction: row;

    .filter-search {

        label {
            color: $text-color;
        }
    }

    &.is-disabled {
        opacity: 0.4;
        pointer-events: none;
    }

    > .dropdown-menu {
        min-width: 280px; // fit on iphone 5 screens
        padding: 1rem;
    }

    .btn-clear-filters {
        color: $brand-primary;
        text-transform: uppercase;
    }

    .btn-filter {

        .badge {
            margin: 0 0.5rem;
        }
    }
}

.filter-group {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: flex-start;
    position: relative;

    margin-right: 2rem;
    padding-right: 2rem;

    &.is-disabled {

        label {
            cursor: not-allowed;
        }

        input {
            pointer-events: none;
        }

        &:before {
            position: absolute;
            content: "";
            background: rgba(white, 0.5);
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1;
        }
    }

    &:not(:last-child) {
        border-right: 1px solid $gray-lighter;
    }

    h4 {
        text-transform: uppercase;
        white-space: nowrap;
        font-size: 1.2rem;
        margin: 0 0 1rem 0;
    }

    .form-check-label {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: center;
        white-space: nowrap;
        cursor: pointer;

        span {
            color: $text-color;
        }

        &.is-disabled {
            color: $gray-light;
        }

        &:not(:first-child) {
            margin-top: 0.5rem;
        }

        input[type="checkbox"], input[type="radio"] {
            margin: 0 0.5rem 0 0;
        }
    }

    @media (max-width: $screen-sm-max) {
         &:not(:last-child) {
            border-right: none;
            margin-bottom: 1.5rem;
        }
    }
}
</style>
